import React from "react";
import { Drawer } from "@mui/material";
import DrawerHeader from "../../shared/helpers/DrawerHeader.js";
import ButtonFilled from "../../shared/helpers/ButtonFilled.js";
import ModalInfoCard from "../../shared/helpers/ModalInfoCard.js";
import ActionPill from "./ActionPill.js";
import { formatDateReadable } from "../../utilities/helpers.js";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import {
  CalendarToday,
  AccessTime,
  FreeBreakfastOutlined,
  WorkOutline,
  PlaceOutlined,
  ChatBubbleOutline,
  CheckCircle,
  PlayArrow,
  PersonOffOutlined,
  PersonOutline
} from '@mui/icons-material';

const ShiftPoolApprovedDrawer = (props) => {
  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    props.setDrawerOpen(open);
  };

  // Determine if this was a drop or open shift based on status
  const isOpenShift = props.shiftInfo.status === "open_shift";
  const isDroppedShift = !isOpenShift && (props.shiftInfo.firstName && props.shiftInfo.lastName);

  // Get the employee rejection list
  const employeeRejectedList = props.shiftInfo.employeeRejectedList || [];

  // Function to get action display for the drawer (same logic as table)
  const getActionDisplay = (shiftInfo) => {
    const status = shiftInfo?.status;
    const employeePickup = shiftInfo?.employeePickup; // You may need to pass this from the parent

    switch (status) {
      case 'drop_accepted':
        if (employeePickup?.first_name && employeePickup?.last_name) {
          return {
            text: `Assigned to ${employeePickup.first_name} ${employeePickup.last_name}`,
            textColor: 'text-emerald-800',
            bgColor: 'bg-emerald-100'
          };
        }
        return {
          text: 'Drop Accepted',
          textColor: 'text-emerald-800',
          bgColor: 'bg-emerald-100'
        };

      case 'drop_rejected':
        return {
          text: 'Drop Rejected',
          textColor: 'text-red-800',
          bgColor: 'bg-red-100'
        };

      case 'drop_to_deleted':
        return {
          text: 'Shift Deleted',
          textColor: 'text-slate-800',
          bgColor: 'bg-slate-100'
        };

      case 'drop_to_open_shift':
        return {
          text: 'Converted to Open Shift',
          textColor: 'text-blue-800',
          bgColor: 'bg-blue-100'
        };

      case 'open_shift':
        if (employeePickup?.first_name && employeePickup?.last_name) {
          return {
            text: `Assigned to ${employeePickup.first_name} ${employeePickup.last_name}`,
            textColor: 'text-emerald-800',
            bgColor: 'bg-emerald-100'
          };
        }
        return {
          text: 'Open Shift',
          textColor: 'text-blue-800',
          bgColor: 'bg-blue-100'
        };

      default:
        return {
          text: 'Pending',
          textColor: 'text-yellow-800',
          bgColor: 'bg-yellow-100'
        };
    }
  };

  return (
    <Drawer anchor="right" open={props.isDrawerOpen} onClose={toggleDrawer(false)}>
      <div className="w-[450px] h-full bg-white relative">
        <div className="max-h-screen overflow-auto">
          <DrawerHeader
            title="Shift Pool History"
            onClose={toggleDrawer(false)}
          />

          <div className="mt-8 px-6">
            {/* Status Banner */}
            {isOpenShift ? (
              <div className="w-full bg-emerald-50 rounded-xl border border-emerald-100 p-3 flex flex-row items-center space-x-1.5">
                <div className="text-emerald-600">
                  <AddCircleIcon />
                </div>
                <p className="text-emerald-800 text-lg">Open shift</p>
              </div>
            ) : isDroppedShift ? (
              <div className="w-full bg-rose-50 rounded-xl border border-rose-100 p-3 flex flex-row items-center space-x-1.5">
                <div className="text-rose-600">
                  <RemoveCircleIcon />
                </div>
                <p className="text-rose-800 text-lg">Dropped by</p>
                <div className="flex flex-row items-center space-x-1.5 pl-2">
                  <p className="text-slate-500 text-lg">
                    {props.shiftInfo.firstName} {props.shiftInfo.lastName}
                  </p>
                </div>
              </div>
            ) : (
              <div className="w-full bg-slate-50 rounded-xl border border-slate-100 p-3 flex flex-row items-center space-x-1.5">
                <div className="text-slate-600">
                  <AddCircleIcon />
                </div>
                <p className="text-slate-800 text-lg">Shift History</p>
              </div>
            )}

            {/* Shift Details */}
            <div className="w-full mt-6">
              <div className="mb-4 flex items-center space-x-4">
                <div className="flex-1">
                  <ModalInfoCard
                    icon={CalendarToday}
                    title="Date"
                    value={formatDateReadable(props.shiftInfo.date)}
                  />
                </div>
                <div className="flex-1">
                  <ModalInfoCard
                    icon={AccessTime}
                    title="Time"
                    value={props.shiftInfo.shift}
                  />
                </div>
              </div>

              <div className="mb-4 flex items-center space-x-4">
                <div className="flex-1">
                  <ModalInfoCard
                    icon={FreeBreakfastOutlined}
                    title="Break"
                    value={props.shiftInfo.break}
                  />
                </div>
                <div className="flex-1">
                  <ModalInfoCard
                    icon={WorkOutline}
                    title="Role"
                    value={
                      props.shiftInfo.station
                        ? props.shiftInfo.station
                        : props.shiftInfo.role
                        ? props.shiftInfo.role
                        : props.shiftInfo.roleType
                    }
                  />
                </div>
              </div>

              {props.note && (
                <div className="mb-4">
                  <ModalInfoCard
                    icon={ChatBubbleOutline}
                    title="Shift Note"
                    value={props.note}
                  />
                </div>
              )}

              {props.employeeDropNote && (
                <div className="mb-4">
                  <ModalInfoCard
                    icon={ChatBubbleOutline}
                    title="Employee Drop Note"
                    value={props.employeeDropNote}
                  />
                </div>
              )}

              {props.shiftInfo?.location && (
                <div className="mb-4">
                  <ModalInfoCard
                    icon={PlaceOutlined}
                    title="Location"
                    value={props.shiftInfo?.location}
                  />
                </div>
              )}

              <div className="mb-4">
                <div className="rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex flex-row items-center text-gray-700">
                      <PlayArrow style={{ fontSize: "18px" }} />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        Action Taken
                      </span>
                    </div>
                  </div>
                  <div className="px-4 py-3">
                    <ActionPill
                      text={getActionDisplay(props.shiftInfo).text}
                      textColor={getActionDisplay(props.shiftInfo).textColor}
                      bgColor={getActionDisplay(props.shiftInfo).bgColor}
                    />
                  </div>
                </div>
              </div>

              {/* Employee Rejection List */}
              {employeeRejectedList.length > 0 && (
                <div className="mb-4">
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                      <div className="flex flex-row items-center text-gray-700">
                        <PersonOffOutlined style={{ fontSize: "18px" }} />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                          Denied Employee Bids ({employeeRejectedList.length})
                        </span>
                      </div>
                    </div>
                    <div className="px-4 py-3">
                      <div className="space-y-2">
                        {employeeRejectedList?.map((employee, index) => (
                          <div key={employee.id || index} className="flex items-center">
                            <ActionPill
                              text={employee.full_name}
                              textColor="text-red-800"
                              bgColor="bg-red-50"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-4 flex items-center space-x-4">
                <div className="flex-1">
                  <ModalInfoCard
                    icon={CheckCircle}
                    title="Date Responded"
                    value={formatDateReadable(props.shiftInfo.dateResponded)}
                  />
                </div>
                {props.shiftInfo.actedBy && (
                  <div className="flex-1">
                    <ModalInfoCard
                      icon={PersonOutline}
                      title="Responded By"
                      value={props.shiftInfo.actedBy}
                    />
                  </div>
                )}
              </div>

            </div>

            {/* Spacer to ensure footer doesn't overlap content */}
            <div className="w-full h-[200px]"></div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="w-full absolute bottom-0 bg-white border-t border-slate-300 px-6">
          <div className="pb-4">
            <div className="w-full mt-4">
              <ButtonFilled
                text="Close"
                onClick={toggleDrawer(false)}
              />
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default ShiftPoolApprovedDrawer;
